import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { Banner, Button, Empty, ErrorBanner, Panel, Spinner } from '@shop/ui'

type Op = {
  clientId: string
  userId: string
  userName: string | null
  kind: string
  occurredAt: string
  status: string
  serverId: string | null
  errorCode: string | null
  error: string | null
  attempts: number
  receivedAt: string
  payload: string
}

const KIND: Record<string, string> = {
  SHIFT_OPEN: 'فتحُ ورديّة',
  SALE: 'بيعة',
  PAYOUT: 'مصروفٌ من الدرج',
  PUNCH: 'دوام',
  SHIFT_CLOSE: 'إغلاقُ ورديّة',
}

const when = new Intl.DateTimeFormat('ar-JO-u-nu-latn', {
  timeZone: 'Asia/Amman', dateStyle: 'medium', timeStyle: 'short',
})

/**
 * ما جرى دون اتّصال ولم يُرحَّل — <b>ليراه المالك ويعالجه</b> (`0052`).
 *
 * ⚠ **فاتورةٌ بيعت وقُبض مالُها ثمّ رُفضت حقيقةٌ لا تُسقط صامتة.** تُعرض هنا
 *   بما أُرسل وبسبب الرفض، فيُدخلها المالكُ بيده أو يعالج سببَها، ثمّ «عولجت».
 */
export default function OfflineOps() {
  const { can } = useAuth()
  const queries = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)

  const failed = useQuery({
    queryKey: ['offline-failed'],
    queryFn: () => api<Op[]>('/api/v1/offline/ops/failed'),
    enabled: can(Perm.cashSessionView),
  })

  if (!can(Perm.cashSessionView)) {
    return (
      <Panel title="عمليّاتٌ دون اتّصال">
        <Banner tone="warn">هذه الشاشةُ تحتاج صلاحيّةَ «رؤيةُ ورديّات الآخرين».</Banner>
      </Panel>
    )
  }

  const dismiss = async (id: string) => {
    setBusy(id)
    setError(null)

    try {
      await api(`/api/v1/offline/ops/${id}/dismiss`, { method: 'POST' })
      await queries.invalidateQueries({ queryKey: ['offline-failed'] })
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Panel title="عمليّاتٌ دون اتّصال لم تُرحَّل">
      <p className="text-sm text-ink-soft">
        ما سُجّل في برنامج الصندوق والنت مقطوع، ثمّ رفضه الخادمُ حين وصل. عالجه بيدك
        (أدخل الفاتورةَ أو المصروفَ من جديد، أو صحّح سببَ الرفض)، ثمّ اكبس «عولجت».
      </p>

      <ErrorBanner error={error ?? failed.error} />

      {failed.isPending ? (
        <Spinner />
      ) : (failed.data ?? []).length === 0 ? (
        <div className="mt-3"><Empty>لا شيء — كلُّ ما جرى دون اتّصال رُحّل.</Empty></div>
      ) : (
        <ul className="mt-3 grid gap-2">
          {(failed.data ?? []).map((op) => (
            <li key={op.clientId} className="rounded-xl border border-danger/40 p-3">
              <div className="flex flex-wrap items-baseline gap-2">
                <b>{KIND[op.kind] ?? op.kind}</b>
                <span className="text-sm text-ink-soft">{op.userName ?? '—'}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{when.format(new Date(op.occurredAt))}</span>
              </div>
              <p className="mt-1 text-sm text-danger">{op.error ?? op.errorCode}</p>
              <details className="mt-1 text-xs">
                <summary className="cursor-pointer text-ink-soft">ما أُرسل</summary>
                <pre className="mt-1 overflow-x-auto rounded bg-surface-raised p-2" dir="ltr">
                  {JSON.stringify(JSON.parse(op.payload), null, 2)}
                </pre>
              </details>
              <div className="mt-2">
                <Button disabled={busy === op.clientId} onClick={() => void dismiss(op.clientId)}>عولجت</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}
